(ns frontend.view.grid.persons-grid.view
  (:require
   [common.attribute :as attribute]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.sync-progress :as sync-progress]
   [common.teams :as teams]
   [frontend.db :as app-db]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.components :as grid-components]
   [frontend.react.virtualized.scrollable-grid.kanban :as kanban]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.grid-filters.view :as grid-filters]
   [frontend.view.entity :as f-entity]
   [frontend.view.grid.common.components :as components]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.ux-overhaul.listing.person.view :as person-listing]
   [frontend.view.grid.persons-grid.db :as db]
   [frontend.view.grid.persons-grid.events :as events]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-modal]
   [frontend.view.named-view.events :as named-view-events]
   [frontend.view.syncing-data :as sync]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.editors.entity-creator.view :as creator-editor]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (partial grid/update-width! (events/setup-args) recompute-grid-sizes! hover-atom attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn toolbar-actions
  [{:keys [view-mode]}]
  (let [all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        account-teams (teams/resolve-account-teams @session/account all-teams)]
    [{:toolbar-button? true
      :dropdown-action? true
      :ord       2
      :hidden?   (fn []
                   (= view-mode :board-mode))
      :primary-button? false
      :add-ellipsis? true
      :on-select (fn [_e]
                   (grid/open-select-columns-curtain (events/setup-args)))
      :label     (loc/tr :action/select-columns)}
     (grid-utils/export-button {:account-teams  account-teams
                                :button-ord     3
                                :grid-state     db/contacts
                                :column-keys-fn events/resolve-column-keys})
     #_(grid-utils/export-with-persons-button {:account-teams  account-teams
                                               :entity-type    :entity.type/person
                                               :grid-state     db/contacts
                                               :button-ord     4
                                               :column-keys-fn events/resolve-column-keys})
     (when-not (session/is-read-only-user?)
       {:toolbar-button? true
        :dropdown-action? true
        :ord       1
        :icon      :fa-plus
        :primary-button? true
        :on-select (fn [_e]
                     (f-entity/open-creator! :entity.type/person {}))
        :label     (loc/tr :contact-add-button)})

     {:divider? true
      :dropdown-action? true
      :ord 14}

     (grid-utils/creator-config-item
      {:ord 15
       :entity-type :entity.type/person})

     (when (= view-mode :board-mode)
       {:toolbar-button? false
        :dropdown-action? true
        :ord       16
        :add-ellipsis? true
        :label     (loc/tr :card-settings)
        :on-select (fn [_e]
                     (events/open-card-settings!))})

     {:divider? true
      :dropdown-action? true
      :ord 17}

     #_{:toolbar-button? true
        :dropdown-action? false
        :ord       4
        :icon      :fa-eraser
        :primary-button? true
        :on-select (fn [e]
                     (events/clear-ls-widths! grid-store))
        :label     (loc/tr :clear-local-storage-widths)}
     (grid-utils/preferred-landing-page-toggle {:view-id :persons-grid})]))

(defn toolbar-buttons-renderer
  []
  (let [setup-args (events/setup-args)
        modal-atom (:modal-state setup-args)
        open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
        selected (grid/get-selected-items setup-args)]
    [:div
     {:class "flex gap-2"}
     (if (session/is-read-only-user?)
       [:span (loc/tr :no-grid-actions-for-read-only-user)]
       [:<>
        [components/toolbar-delete-button
         {:max-delete-count   10000
          :selected-items     selected
          :on-delete-clicked! events/delete-selected-clicked}]
        [components/toolbar-bulk-edit-button
         {:max-bulk-edit-count   10000
          :selected-items        selected
          :on-bulk-edit-clicked! open-bulk-edit!}]
        (when (session/entity-merge?)
          [components/toolbar-merge-button
           {:selected-items selected
            :entity-type :entity.type/person}])])]))

(defn create-props-atom
  [selected sorted-contacts filters grid-store
   recompute-grid-sizes! hover-atom modal-atom]
  (let [tenant-schema @session/tenant-schema
        columns
        (->> (events/resolve-column-keys)
             (keep (fn [k]
                     (let [field-schema (registry/get-field-schema tenant-schema k)
                           label (attribute/attribute-label tenant-schema k)]
                       (column {:label             label
                                :attribute         k
                                :field-schema      field-schema
                                :filter-data       (get filters k)
                                :sorting-disabled? false
                                :width             (grid/get-saved-width (events/setup-args) k)
                                :hover-atom        hover-atom
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :flex-grow         1}))))
             (vec))
        editable-grid? (and (session/has-feature-access? :feature/editable-grid)
                            @session/editable-grid?)]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args)
      :grid-entity-ids         (->> sorted-contacts
                                    (mapv :db/id)
                                    (into #{}))
      :selected                selected
      :link-to-entity-field    :person/first-name
      :header-height           50
      :editable-grid?          editable-grid?
      :select-width            select-width
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400)
                                  75)                       ;; TODO: lazy - fixed 120px for button row in this view
      :avail-height            (- (or (:height @app-db/window) 400)
                                  75) ;; TODO: lazy - fixed 120px for button row in this view
      :useDynamicRowHeight     false  ;; TODO: kebab-case
      :row-height              40     ;(fn [index] 40)
      :row-count               (if (sequential? sorted-contacts) (count sorted-contacts) 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :no-rows-renderer        (r/partial grid/default-no-rows-render
                                          @db/contacts
                                          :entity.type/person)
      :on-row-click!           (fn [row]
                                 (let [data (when (< row (count sorted-contacts))
                                              (nth sorted-contacts row))]
                                   (routing/open-entity (:db/id data))))
      :on-cell-click           (fn [row col]
                                 (grid/handle-cell-click! {:columns columns
                                                           :col     col
                                                           :row     row
                                                           :sorted-grid-entities sorted-contacts
                                                           :state   hover-atom}))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args) k dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args)
                                                        dragged-column
                                                        after-column))
      :can-filter?             (fn [_k]
                                 true)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :all-selection!          (fn []
                                 (let [selected (:selected @db/contacts)]
                                   (if (or (= :all selected) (not-empty selected))
                                     (grid/clear-selection! (events/setup-args))
                                     (swap! db/contacts assoc :selected :all))))
      :select-action-renderer  toolbar-buttons-renderer
      :on-row-select!          (fn [index checked]
                                 (let [data (when (< index (count sorted-contacts))
                                              (nth sorted-contacts index))
                                       contact-id (:db/id data)]
                                   (if (= selected :all)
                                     (grid/select-all-but (events/setup-args) contact-id)
                                     (if (boolean checked)
                                       (swap! db/contacts assoc-in [:selected contact-id] true)
                                       (swap! db/contacts potpuri/dissoc-in
                                         [:selected contact-id])))))
      :deselect-entity!        (fn [entity-id]
                                 (if (= selected :all)
                                   (grid/select-all-but (events/setup-args) entity-id)
                                   (swap! db/contacts potpuri/dissoc-in [:selected entity-id])))
      :row-data-getter         (r/partial grid/default-row-data-getter sorted-contacts selected)

      :new-action-view         {:in-use?         true
                                :open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
                                :bulk-delete!    events/delete-selected-clicked
                                :modal-atom      modal-atom}})))

(defn toolbar
  [{:keys [view-mode]}]
  (let [board-mode? (= view-mode :board-mode)
        card-mode (or (:card-mode @db/kanban)
                      :short-card)
        has-empty-groups? (when board-mode?
                            (kanban/has-empty-groups? @(r/track events/setup-args)))]
    [:div
     {:class "px-4 py-1 flex items-center justify-between
                   bg-base-light
                   border-b border-solid border-base-light-border-paler"}
     [:div
      {:class "w-full flex justify-between gap-6"}
      [:div
       [:div
        {:class "flex gap-2 items-center"}
        [:div
         {:class "flex items-center gap-2"}
         [:label
          {:class "font-semibold"}
          (loc/tr :persons-grid)]

        [:span
         {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
         (count @db/sorted-kanban-entities)]]

        [grid-components/named-view-selector
         {:named-views        (events/named-views)
          :current-view       @db/view-settings
          :load-view!         (fn [id]
                                (events/load-view! (events/named-view-by-id id))
                                (grid/clear-selection! {:state db/contacts}))
          :delete-view!       named-view-events/start-delete-view!
          :accounts-with-view-access (session/active-accounts nil)
          :save-current-view! events/save-current-view!}]

        (when board-mode?
          [grid-components/sort-selector
           {:view-settings   @db/view-settings
            :entity-type     :entity.type/person
            :sortable-fields (events/resolve-column-key-universe)
            :set-sort-by!    (fn [k dir]
                               (grid/set-sort-by (events/setup-args) k dir))}])

        (when board-mode?
          [grid-components/grouped-by-selector
           {:view-settings      @db/view-settings
            :entity-type        :entity.type/person
            :default-grouped-by :person/contact-owner
            :grouped-by-fields  (->> (events/resolve-column-key-universe)
                                     (filter grid/groupable-attribute?))
            :set-grouped-by!    (fn [k]
                                  (swap! db/view-settings assoc :grouped-by k)
                                  (ls/update-in (events/ls-path)
                                                assoc
                                                :grouped-by
                                                k))}])]]
      (when (and (session/has-feature-access? :feature/enrichment-clearbit)
                 (:account/nylas-account-id @session/account)
                 (:account/nylas-initial-sync-started-on @session/account)
                 (not (:account/nylas-initial-sync-completed-on @session/account)))
        [:div
         {:style {:flex "1 1 100%"}}
         [:span
          (loc/tr :initial-automatic-contact-creation-in-progress)]])
      [:div
       {:class "flex justify-content-end items-center gap-2"}
       ;;(when-not board-mode?
       ;;  [grid-components/editable-grid-toggle])
       (when (and board-mode? (not= card-mode :short-card))
         [button/square-secondary-open
          {:on-click (fn [_] (events/collapse-all!))
           :tooltip  (loc/tr :collapse-all)}
          [:i.fal.fa-compress-alt]])
       (when (and board-mode? (= card-mode :short-card))
         [button/square-secondary-open
          {:tooltip (loc/tr :expand-all)
           :on-click   (fn [_] (events/expand-all!))}
          [:i.fal.fa-expand-alt]])

       [button/square-secondary-open
        {:tooltip (grid-components/view-mode-tooltip board-mode?)
         :on-click   (fn [_]
                       (events/switch-mode! view-mode))}
        (if board-mode?
          [:i.fal.fa-table]
          [:i.fal.fa-signal-3.fa-rotate-180.kanban-icon])]

       (when (and board-mode? has-empty-groups?)
         [grid-utils/hide-empty-kanban-groups-toggle "persons-grid-toolbar" db/kanban])

       [:div
        (zap-toolbar/dropdown (->> (toolbar-actions {:view-mode view-mode})
                                   (filter :dropdown-action?)
                                   (remove (fn [{:keys [hidden?]}]
                                             (when (fn? hidden?)
                                               (hidden?))))
                                   (sort-by :ord))
                              true)]]]]))

(defn render
  [grid-store _args]
  (let [hover-atom (r/atom {:hover {:col nil :row nil}})

        entity-type :entity.type/person

        ;;for saving grid references:
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [args]
      (let [tenant-schema @session/tenant-schema
            selected (:selected @db/contacts)
            filters {}
            #_@db/contact-filters                           ;;TODO later
            sorted-contacts (:sorted-grid-entities @db/contacts)
            view-mode (or (:view-mode @db/view-settings)
                          (:view-mode (ls/get-in (events/ls-path)))
                          (:initial-mode args)
                          :grid-mode)
            card-settings? (:card-settings? @db/kanban)
            setup-args @(r/track events/setup-args)
            modal-atom (:modal-state setup-args)
            grid-atom (create-props-atom selected
                                         sorted-contacts
                                         filters
                                         grid-store
                                         recompute-grid-sizes!
                                         hover-atom
                                         modal-atom)
            show-quick-filters? (:show-quick-filters? @db/contacts)
            show-bulk-edit-modal? (:bulk-edit-modal @modal-atom)
            filters-args {:minimized?            (not show-quick-filters?)
                          :hide-maximize?        true
                          :universal-search?     true
                          :search-placeholder    (loc/tr :search-for-a-contact)
                          :setup-args            (events/setup-args)
                          :set-universal-search! (r/partial grid/set-universal-search! setup-args)
                          :set-name-search!      (r/partial grid/set-name-search! setup-args)
                          :hide-named-views?     true
                          :named-views           (events/named-views)
                          :current-view          @db/view-settings
                          :columns               (events/resolve-column-keys)
                          :all-columns           (events/resolve-column-key-universe)
                          :get-column-label      (fn [a]
                                                   (attribute/attribute-label @session/tenant-schema a))
                          :set-filter!           (fn [k filter-data]
                                                   (grid/set-filter (events/setup-args) k filter-data))
                          :edit-filter!          (fn [k filter-data]
                                                   (grid/open-filter-curtain
                                                    {:field-key      k
                                                     :field-schema   (registry/get-field-schema tenant-schema k)
                                                     :options        (->> (events/resolve-column-key-universe)
                                                                          (map (juxt identity
                                                                                     (partial
                                                                                      registry/get-field-schema
                                                                                      tenant-schema))))
                                                     :current-filter filter-data
                                                     :update-fn!     (fn [k filter-data]
                                                                       (grid/set-filter (events/setup-args)
                                                                                        k
                                                                                        filter-data))}
                                                    nil))
                          :load-view!            (fn [named-view]
                                                   (events/load-view! named-view)
                                                   (grid/clear-selection! {:state db/contacts}))
                          :save-view!            events/save-current-view!
                          :delete-view!          named-view-events/delete-view!
                          :toggle-minimize!      (fn []
                                                   (events/toggle-quick-filters!)
                                                   (.setTimeout js/window recompute-grid-sizes! 1000))}]


        (if card-settings?
          [kanban/card-settings-view (events/setup-args)]
          [:<>
           [grid-filters/view
            filters-args]
           [creator-editor/modal-view]
           [:div
            {:class "flex flex-auto ps-4"}
            [:div
             {:class-name (fu/cs "grid-view-content"        ;;"contacts-view-content"
                                 (when-not show-quick-filters? "named-views-minimized")
                                 (when (= view-mode :board-mode) "board-view"))}

             (when show-bulk-edit-modal?
               [bulk-edit-modal/view
                {:setup-args   (events/setup-args)
                 :entity-type  entity-type
                 :close-modal! #(swap! modal-atom dissoc :bulk-edit-modal)}])

             (if (= view-mode :board-mode)
               [kanban/view
                {:setup-args   (events/setup-args)
                 :card-actions [(kanban/open-entity-action
                                 {:entity-type :entity.type/person})]}]
               [scrollable-grid/view
                {:props-atom grid-atom
                 :hover-atom hover-atom}])]]])))))

(defn view
  [_args]
  (let [grid-store (atom {})
        close-atom (atom false)
        resizer (fn []
                  (js/setTimeout (partial vu/recompute-grid-sizes-with-state! grid-store) 100))]
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn []
                                (fu/timeout 10 (partial events/setup close-atom)))
      :component-will-unmount (fn []
                                (try
                                  (reset! close-atom true)
                                  (events/cleanup)
                                  (.removeEventListener js/window "resize" resizer)
                                  (catch js/Object e
                                    (logging/error "ERR : " e))))
      :reagent-render         (partial render grid-store)})))

(defmethod routing/render-view :persons-grid
  []
  (if (session/new-grid-2023?)
    [default-page-layout/view
     [:<>
      [person-listing/page-content]]]
    (let [view-mode (or (:view-mode @db/view-settings)
                        (:view-mode (ls/get-in (events/ls-path)))
                        :grid-mode)]
      [default-page-layout/view
       [sync/wait-until-sync-progress sync-progress/CONTACTS
        [:<>
         [components/card-settings-toolbar
          [toolbar {:view-mode view-mode}] db/kanban events/close-card-settings!]
         [view {}]]]])))
